import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid2 as Grid,
  TextField,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  People,
  PersonAdd,
  AdminPanelSettings,
  Business,
  Person,
  Refresh,
  Save,
  Delete,
  CheckCircle,
  HourglassEmpty,
  LockReset,
  LockOpen,
  Campaign,
  Send,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Divider,
  LinearProgress,
} from '@mui/material';
import DataTable, { Column, StatusChip, DateCell } from '../../../components/Common/DataTable';
import { usersService } from '../../../services';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';

interface UserData {
  id: string;
  username: string;
  email: string;
  name?: string;
  perfil: string;
  perfilCodigo: string;
  estado: string;
  dataRegisto: string;
  ultimoLogin: string | null;
  tipoConta?: string;
  loginAttempts?: number;
  lockedUntil?: number | null; // epoch em segundos
}

/** Bloqueio de login: permanente (ESTADO 'B') ou temporário por tentativas falhadas. */
const isLocked = (u: UserData) =>
  u.estado === 'B' || (!!u.lockedUntil && u.lockedUntil * 1000 > Date.now());

interface UserStats {
  total: number;
  ativos: number;
  inativos: number;
  admins: number;
}

const Users: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [statusFilter, setStatusFilter] = useState('A');
  const [perfilFilter, setPerfilFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [successMsg, setSuccessMsg] = useState('');

  // Edit/Create dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null); // null = create mode
  const [editForm, setEditForm] = useState({ username: '', email: '', name: '', password: '', perfilId: 'PFCLI', estado: 'A' });
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete confirm
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<UserData | null>(null);

  // Reset password
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const [stats, setStats] = useState<UserStats>({
    total: 0,
    ativos: 0,
    inativos: 0,
    admins: 0,
  });
  const [pendingDevs, setPendingDevs] = useState(0);
  const [approvingId, setApprovingId] = useState<string | null>(null);

  // Bulk notify dialog
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyFiltroTipo, setNotifyFiltroTipo] = useState<'inativos' | 'sem_actividade' | 'ambos'>('inativos');
  const [notifyDias, setNotifyDias] = useState(90);
  const [notifyCanal, setNotifyCanal] = useState<'email' | 'sms' | 'ambos'>('email');
  const [notifyAssunto, setNotifyAssunto] = useState('');
  const [notifyMensagem, setNotifyMensagem] = useState('');
  const [notifyPreview, setNotifyPreview] = useState<{ total: number; comEmail: number; comTelefone: number } | null>(null);
  const [notifyPreviewLoading, setNotifyPreviewLoading] = useState(false);
  const [notifySending, setNotifySending] = useState(false);
  const [notifyResult, setNotifyResult] = useState<{ total: number; enviados: number; falharam: number } | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        page: page + 1,
        limit: rowsPerPage,
        ...(debouncedSearch && { search: debouncedSearch }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(perfilFilter !== 'all' && { perfil: perfilFilter }),
      };

      const response = await usersService.getUsers(filters);

      // Map API response to component structure
      const mappedUsers = response.data.map((user: any) => ({
        id: user.id,
        username: user.username,
        email: user.email,
        name: user.name,
        perfil: user.perfil || t('users.clientRole'),
        perfilCodigo: user.perfilId || 'PFCLI',
        estado: user.status,
        dataRegisto: user.createdAt,
        ultimoLogin: user.lastLogin,
        tipoConta: user.tipoConta,
        loginAttempts: Number(user.loginAttempts) || 0,
        lockedUntil: user.lockedUntil ? Number(user.lockedUntil) : null,
      }));

      setUsers(mappedUsers);
      setTotalCount(response.pagination?.total || 0);

      if (response.stats) {
        setStats(response.stats);
        if (response.stats.pendingDevelopers) setPendingDevs(response.stats.pendingDevelopers);
      }
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err.message || t('users.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, statusFilter, perfilFilter]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Debounce search: espera 400ms após o utilizador parar de escrever
  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0);
    }, 400);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchQuery]);

  const getPerfilIcon = (perfil: string) => {
    switch (perfil) {
      case 'PFADM':
        return <AdminPanelSettings fontSize="small" />;
      case 'PFENT':
        return <Business fontSize="small" />;
      default:
        return <Person fontSize="small" />;
    }
  };

  const getPerfilColor = (perfil: string): 'primary' | 'secondary' | 'success' | 'warning' | 'info' => {
    switch (perfil) {
      case 'PFADM':
        return 'primary';
      case 'PFENT':
        return 'secondary';
      case 'PFCON':
        return 'warning';
      default:
        return 'info';
    }
  };

  const columns: Column<UserData>[] = [
    {
      id: 'username',
      label: t('users.user'),
      minWidth: 150,
      format: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            {value?.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'perfil',
      label: t('common.profile'),
      minWidth: 130,
      format: (value, row) => (
        <Chip
          icon={getPerfilIcon(row.perfilCodigo)}
          label={value}
          size="small"
          color={getPerfilColor(row.perfilCodigo)}
          variant="outlined"
        />
      ),
    },
    {
      id: 'estado',
      label: t('common.status'),
      minWidth: 140,
      align: 'center',
      format: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          {value === 'B' ? (
            <Chip label="Bloqueada" size="small" color="error" />
          ) : (
            <StatusChip
              status={value === 'A' ? t('common.active') : value === 'P' ? 'Pendente' : t('common.inactive')}
              colorMap={{ [t('common.active')]: 'success', Pendente: 'warning', [t('common.inactive')]: 'default' }}
            />
          )}
          {value !== 'B' && isLocked(row) && (
            <Chip label="Login bloqueado" size="small" color="error" variant="outlined" />
          )}
          {value === 'P' && row.tipoConta === 'DEVELOPER' && (
            <Button
              size="small"
              color="success"
              variant="contained"
              startIcon={approvingId === row.id ? <CircularProgress size={14} color="inherit" /> : <CheckCircle fontSize="small" />}
              onClick={() => handleApprove(row)}
              disabled={approvingId === row.id}
              sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: '0.7rem' }}
            >
              Aprovar
            </Button>
          )}
        </Box>
      ),
    },
    {
      id: 'dataRegisto',
      label: t('common.dateRegister'),
      minWidth: 120,
      format: (value) => <DateCell value={value} />,
    },
    {
      id: 'ultimoLogin',
      label: t('common.lastLogin'),
      minWidth: 150,
      format: (value) =>
        value ? <DateCell value={value} includeTime /> : <span style={{ color: '#999' }}>{t('common.never')}</span>,
    },
  ];

  const handleView = (user: UserData) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleEdit = (user: UserData) => {
    setEditingUser(user);
    setEditForm({
      username: user.username,
      email: user.email,
      name: user.name || '',
      password: '',
      perfilId: user.perfilCodigo || 'PFCLI',
      estado: user.estado,
    });
    setEditDialogOpen(true);
  };

  const handleCreate = () => {
    setEditingUser(null);
    setEditForm({ username: '', email: '', name: '', password: '', perfilId: 'PFCLI', estado: 'A' });
    setEditDialogOpen(true);
  };

  const handleSaveUser = async () => {
    if (!editForm.username.trim() || !editForm.email.trim()) {
      setError(t('users.usernameRequired'));
      return;
    }
    setSavingEdit(true);
    setError(null);
    try {
      if (editingUser) {
        // Update
        const { password, ...updateData } = editForm;
        await usersService.updateUser(editingUser.id, updateData);
        setSuccessMsg(t('users.userUpdated'));
      } else {
        // Create
        if (!editForm.password) {
          setError(t('users.passwordRequired'));
          setSavingEdit(false);
          return;
        }
        await usersService.createUser(editForm);
        setSuccessMsg(t('users.userCreated'));
      }
      setEditDialogOpen(false);
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || t('users.errorSaving'));
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDelete = (user: UserData) => {
    setDeletingUser(user);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletingUser) return;
    try {
      await usersService.deleteUser(deletingUser.id);
      setSuccessMsg(t('users.userDeleted'));
      setDeleteDialogOpen(false);
      setDeletingUser(null);
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || t('users.errorDeleting'));
      setDeleteDialogOpen(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    setResetLoading(true);
    try {
      const response = await api.post(`/admin/users/${selectedUser.id}/reset-password`);
      setResetConfirmOpen(false);
      setSuccessMsg(response.data.message || 'Email de redefinição enviado com sucesso');
    } catch (err: any) {
      setResetConfirmOpen(false);
      setError(err?.response?.data?.error || err.message || 'Erro ao enviar email de reset');
    } finally {
      setResetLoading(false);
    }
  };

  const [unlocking, setUnlocking] = useState(false);
  const handleUnlock = async () => {
    if (!selectedUser) return;
    if (!window.confirm(`Desbloquear o login de ${selectedUser.username}?`)) return;
    setUnlocking(true);
    try {
      const response = await api.post(`/admin/users/${selectedUser.id}/security-unblock`);
      setSuccessMsg(response.data?.message || 'Conta desbloqueada com sucesso');
      setSelectedUser({
        ...selectedUser,
        estado: selectedUser.estado === 'B' ? 'A' : selectedUser.estado,
        loginAttempts: 0,
        lockedUntil: null,
      });
      fetchUsers();
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || 'Erro ao desbloquear conta');
    } finally {
      setUnlocking(false);
    }
  };

  const handleApprove = async (user: UserData) => {
    if (!window.confirm(`Aprovar conta Developer de ${user.username}?`)) return;
    setApprovingId(user.id);
    try {
      await usersService.approveUser(user.id);
      setSuccessMsg(`Conta de ${user.username} aprovada com sucesso!`);
      fetchUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao aprovar conta');
    } finally {
      setApprovingId(null);
    }
  };

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Feedback Alerts */}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('users.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('users.subtitle')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Campaign />}
            onClick={() => {
              setNotifyPreview(null);
              setNotifyResult(null);
              setNotifyMensagem('');
              setNotifyAssunto('');
              setNotifyOpen(true);
            }}
          >
            Notificar Utilizadores
          </Button>
          <Button variant="contained" startIcon={<PersonAdd />} onClick={handleCreate}>
            {t('users.newUser')}
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'primary.light',
                    color: 'primary.main',
                  }}
                >
                  <People />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('users.totalUsers')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'success.light',
                    color: 'success.main',
                  }}
                >
                  <Person />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.ativos}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('users.active')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'action.selected',
                    color: 'text.secondary',
                  }}
                >
                  <Person />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.inativos}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('users.inactive')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'info.light',
                    color: 'info.main',
                  }}
                >
                  <AdminPanelSettings />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {stats.admins}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('users.administrators')}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'warning.light',
                    color: 'warning.main',
                  }}
                >
                  <HourglassEmpty />
                </Box>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {pendingDevs}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Developers Pendentes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField
              fullWidth
              size="small"
              label={t('common.search')}
              placeholder={t('users.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('common.status')}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">{t('common.all')}</MenuItem>
              <MenuItem value="A">{t('users.active')}</MenuItem>
              <MenuItem value="I">{t('users.inactive')}</MenuItem>
              <MenuItem value="P">Pendente</MenuItem>
              <MenuItem value="LOCKED">Bloqueadas (login)</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              fullWidth
              size="small"
              select
              label={t('common.profile')}
              value={perfilFilter}
              onChange={(e) => setPerfilFilter(e.target.value)}
            >
              <MenuItem value="all">{t('common.all')}</MenuItem>
              <MenuItem value="PFADM">{t('users.administrator')}</MenuItem>
              <MenuItem value="PFCLI">{t('users.clientRole')}</MenuItem>
              <MenuItem value="PFENT">{t('users.entityRole')}</MenuItem>
              <MenuItem value="PFCON">{t('users.financialRole')}</MenuItem>
              <MenuItem value="PFDEV">Developer</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchUsers}
            >
              {t('common.refresh')}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Table */}
      <DataTable
        columns={columns}
        data={users}
        loading={loading}
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={(rpp) => {
          setRowsPerPage(rpp);
          setPage(0);
        }}
        onRefresh={fetchUsers}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        rowKey="id"
        emptyMessage={t('users.noUsers')}
      />

      {/* User Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('users.userDetails')}</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: 'primary.main', fontSize: 24 }}>
                  {selectedUser.username.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6">{selectedUser.username}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedUser.email}
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">{t('common.profile')}</Typography>
                  <Typography variant="body2">{selectedUser.perfil}</Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">{t('common.status')}</Typography>
                  <Typography variant="body2">
                    {selectedUser.estado === 'A' ? t('common.active') : selectedUser.estado === 'B' ? 'Bloqueada' : t('common.inactive')}
                  </Typography>
                </Grid>
                {isLocked(selectedUser) && (
                  <Grid size={{ xs: 12 }}>
                    <Alert
                      severity="error"
                      action={
                        <Button color="inherit" size="small" startIcon={unlocking ? <CircularProgress size={14} color="inherit" /> : <LockOpen />}
                          onClick={handleUnlock} disabled={unlocking}>
                          Desbloquear
                        </Button>
                      }
                    >
                      {selectedUser.estado === 'B'
                        ? `Bloqueio permanente · ${selectedUser.loginAttempts || 0} tentativas falhadas`
                        : `Login bloqueado até ${new Date((selectedUser.lockedUntil || 0) * 1000).toLocaleTimeString('pt-PT', { timeZone: 'Atlantic/Cape_Verde', hour: '2-digit', minute: '2-digit' })} · ${selectedUser.loginAttempts || 0} tentativas falhadas`}
                    </Alert>
                  </Grid>
                )}
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">{t('common.dateRegister')}</Typography>
                  <Typography variant="body2">
                    {new Date(selectedUser.dataRegisto).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="caption" color="text.secondary">{t('common.lastLogin')}</Typography>
                  <Typography variant="body2">
                    {selectedUser.ultimoLogin
                      ? new Date(selectedUser.ultimoLogin).toLocaleString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })
                      : t('common.never')}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t('common.close')}</Button>
          <Button variant="outlined" color="warning" startIcon={<LockReset />}
            onClick={() => setResetConfirmOpen(true)}
            disabled={selectedUser?.estado !== 'A' || !selectedUser?.email}>
            Redefinir Senha
          </Button>
          <Button variant="contained" onClick={() => { setDialogOpen(false); handleEdit(selectedUser!); }}>
            {t('common.edit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Create User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => { setEditDialogOpen(false); setError(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{editingUser ? t('users.editUser') : t('users.newUser')}</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label={`${t('common.username')} *`} value={editForm.username}
                disabled={!!editingUser}
                onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label={`${t('common.email')} *`} type="email" value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" label={t('common.name')} value={editForm.name}
                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </Grid>
            {!editingUser && (
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField fullWidth size="small" label={`${t('common.password')} *`} type="password" value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" select label={t('common.profile')} value={editForm.perfilId || 'PFCLI'}
                onChange={(e) => setEditForm({ ...editForm, perfilId: e.target.value })}>
                <MenuItem value="PFADM">{t('users.administrator')}</MenuItem>
                <MenuItem value="PFCLI">{t('users.clientRole')}</MenuItem>
                <MenuItem value="PFENT">{t('users.entityRole')}</MenuItem>
                <MenuItem value="PFCON">{t('users.financialRole')}</MenuItem>
                <MenuItem value="PFDEV">Developer</MenuItem>
                <MenuItem value="PFAGT">Agente</MenuItem>
                <MenuItem value="PFOPE">Operador</MenuItem>
                <MenuItem value="PFLOJ">Lojista</MenuItem>
                <MenuItem value="PFEMP">Empresa</MenuItem>
                <MenuItem value="PFGOV">Governo</MenuItem>
                <MenuItem value="PFONG">ONG</MenuItem>
                <MenuItem value="PFEVT">Eventos</MenuItem>
                <MenuItem value="PFPRO">Profissional</MenuItem>
                <MenuItem value="PFFOD">Restaurante</MenuItem>
                <MenuItem value="PFCHF">Chef/Cozinha</MenuItem>
                <MenuItem value="PFGAR">Garçom</MenuItem>
                <MenuItem value="PFMOT">Entregador (Driver)</MenuItem>
                <MenuItem value="PFCOL">Colaborador</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth size="small" select label={t('common.status')} value={editForm.estado}
                onChange={(e) => setEditForm({ ...editForm, estado: e.target.value })}>
                <MenuItem value="A">{t('common.active')}</MenuItem>
                <MenuItem value="I">{t('common.inactive')}</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setEditDialogOpen(false); setError(null); }}>{t('common.cancel')}</Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSaveUser} disabled={savingEdit}>
            {savingEdit ? t('common.saving') : t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Confirmation Dialog */}
      <Dialog open={resetConfirmOpen} onClose={() => setResetConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Redefinir Senha</DialogTitle>
        <DialogContent>
          <Typography>
            Tem a certeza que deseja enviar um email de redefinição de senha para{' '}
            <strong>{selectedUser?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            O link será válido durante 60 minutos.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirmOpen(false)} disabled={resetLoading}>
            {t('common.cancel')}
          </Button>
          <Button variant="contained" color="warning" startIcon={<LockReset />}
            onClick={handleResetPassword} disabled={resetLoading}>
            {resetLoading ? 'A enviar...' : 'Enviar Email'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('common.confirm')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('users.confirmDelete')} <strong>{deletingUser?.username}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" startIcon={<Delete />} onClick={handleConfirmDelete}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Notify Dialog */}
      <Dialog open={notifyOpen} onClose={() => !notifySending && setNotifyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Campaign color="primary" />
          Notificar Utilizadores
        </DialogTitle>
        <DialogContent dividers>
          {notifyResult ? (
            <Box sx={{ py: 2, textAlign: 'center' }}>
              <CheckCircle sx={{ fontSize: 56, color: 'success.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Notificações enviadas</Typography>
              <Typography>Total de utilizadores: <strong>{notifyResult.total}</strong></Typography>
              <Typography color="success.main">Enviadas com sucesso: <strong>{notifyResult.enviados}</strong></Typography>
              {notifyResult.falharam > 0 && (
                <Typography color="error">Falharam: <strong>{notifyResult.falharam}</strong></Typography>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
              {/* Filtro */}
              <FormControl>
                <FormLabel sx={{ fontWeight: 600, mb: 1 }}>Destinatários</FormLabel>
                <RadioGroup
                  value={notifyFiltroTipo}
                  onChange={(e) => { setNotifyFiltroTipo(e.target.value as any); setNotifyPreview(null); }}
                >
                  <FormControlLabel value="inativos" control={<Radio />} label="Contas desativadas (estado = Inativo)" />
                  <FormControlLabel value="sem_actividade" control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <span>Sem actividade há mais de</span>
                        <TextField
                          size="small"
                          type="number"
                          value={notifyDias}
                          onChange={(e) => { setNotifyDias(parseInt(e.target.value) || 90); setNotifyPreview(null); }}
                          inputProps={{ min: 1, max: 3650, style: { width: 64, textAlign: 'center' } }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <span>dias</span>
                      </Box>
                    }
                  />
                  <FormControlLabel value="ambos" control={<Radio />} label="Ambas as condições" />
                </RadioGroup>
              </FormControl>

              <Divider />

              {/* Canal */}
              <FormControl>
                <FormLabel sx={{ fontWeight: 600, mb: 1 }}>Canal</FormLabel>
                <RadioGroup row value={notifyCanal} onChange={(e) => setNotifyCanal(e.target.value as any)}>
                  <FormControlLabel value="email" control={<Radio />} label="Email" />
                  <FormControlLabel value="sms" control={<Radio />} label="SMS" />
                  <FormControlLabel value="ambos" control={<Radio />} label="Email + SMS" />
                </RadioGroup>
              </FormControl>

              <Divider />

              {/* Mensagem */}
              {(notifyCanal === 'email' || notifyCanal === 'ambos') && (
                <TextField
                  label="Assunto do email"
                  value={notifyAssunto}
                  onChange={(e) => setNotifyAssunto(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder="Ex: Sentimos a sua falta na Pagali"
                />
              )}
              <TextField
                label="Mensagem"
                value={notifyMensagem}
                onChange={(e) => setNotifyMensagem(e.target.value)}
                fullWidth
                multiline
                rows={5}
                placeholder="Escreva a mensagem a enviar aos utilizadores..."
              />

              {/* Preview */}
              {notifyPreview && (
                <Alert severity="info" icon={false}>
                  <Typography variant="body2">
                    <strong>{notifyPreview.total}</strong> utilizadores encontrados
                    {notifyPreview.comEmail > 0 && ` · ${notifyPreview.comEmail} com email`}
                    {notifyPreview.comTelefone > 0 && ` · ${notifyPreview.comTelefone} com telefone`}
                  </Typography>
                </Alert>
              )}

              {notifySending && <LinearProgress />}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyOpen(false)} disabled={notifySending}>
            {notifyResult ? 'Fechar' : 'Cancelar'}
          </Button>
          {!notifyResult && (
            <>
              <Button
                startIcon={<SearchIcon />}
                disabled={notifyPreviewLoading || notifySending}
                onClick={async () => {
                  setNotifyPreviewLoading(true);
                  try {
                    const params = new URLSearchParams({ tipo: notifyFiltroTipo, dias: String(notifyDias) });
                    const res = await api.get(`/admin/users/bulk-notify/preview?${params}`);
                    setNotifyPreview(res.data);
                  } catch { /* ignore */ } finally { setNotifyPreviewLoading(false); }
                }}
              >
                {notifyPreviewLoading ? 'A verificar...' : 'Verificar'}
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                disabled={notifySending || !notifyMensagem.trim() || (notifyPreview !== null && notifyPreview.total === 0)}
                onClick={async () => {
                  if (!notifyMensagem.trim()) return;
                  setNotifySending(true);
                  try {
                    const res = await api.post('/admin/users/bulk-notify', {
                      canal: notifyCanal,
                      assunto: notifyAssunto,
                      mensagem: notifyMensagem,
                      filtro: { tipo: notifyFiltroTipo, dias: notifyDias },
                    });
                    setNotifyResult(res.data);
                  } catch { /* ignore */ } finally { setNotifySending(false); }
                }}
              >
                {notifySending ? 'A enviar...' : 'Enviar'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
